import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Brain, ClipboardCopy, Copy, Check, Eye, EyeOff, Terminal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, formatCurrency, formatDuration } from '@/frontend/pages/CommandCenter/Telemetry/utils'
import { ConversationView } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import { AllEventsView } from '@/frontend/pages/CommandCenter/shared/AllEventsView'
import type { FleetSessionEvent } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import type { Session } from '../types'

interface Props {
  session: Session
  events: FleetSessionEvent[]
  viewMode: string
  showThinking: boolean
  showToolCalls: boolean
  eventRole: string
  basePath: string
  currentParams: Record<string, string | number | undefined>
}

export default function ConversationPanel({
  session,
  events,
  viewMode,
  showThinking,
  showToolCalls,
  eventRole,
  basePath,
  currentParams,
}: Props) {
  const [copied, setCopied] = useState(false)
  const [copiedJson, setCopiedJson] = useState(false)
  const conversationRef = useRef<HTMLDivElement>(null)
  const isConversation = viewMode === 'conversation'

  // Scroll to bottom (most recent) when session changes
  useEffect(() => {
    const el = conversationRef.current
    if (el) el.scrollTo(0, el.scrollHeight)
  }, [session.sessionId])

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(events, null, 2))
    setCopiedJson(true)
    setTimeout(() => setCopiedJson(false), 2000)
  }

  const handleCopySessionId = () => {
    navigator.clipboard.writeText(session.sessionId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const navigateWithParams = (overrides: Record<string, string | number | undefined>) => {
    const params: Record<string, string | number | undefined> = {
      ...currentParams,
      ...overrides,
    }
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )
    router.get(basePath, cleanParams, { preserveState: true, preserveScroll: true, replace: true })
  }

  const toggleViewMode = (mode: string) => {
    navigateWithParams({ view_mode: mode, event_page: 1, event_role: mode === 'conversation' ? undefined : eventRole || undefined })
  }

  const handleRoleFilter = (value: string) => {
    navigateWithParams({ event_role: value || undefined, event_page: 1 })
  }

  const spansMultipleDays = events.length >= 2 && (() => {
    const first = new Date(events[0].timestamp)
    const last = new Date(events[events.length - 1].timestamp)
    return first.toDateString() !== last.toDateString()
  })()

  const duration = formatDuration(session.startedAt, session.lastEventAt)
  const stats = [
    `${formatNumber(session.totalEvents)} eventos`,
    `${formatNumber(session.totalTokens)} tokens`,
    formatCurrency(session.totalCost),
    duration !== '—' ? duration : null,
    session.lastChannel,
    session.model,
  ].filter(Boolean).join(' · ')

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-background flex-shrink-0">
        <h3 className="text-sm font-semibold text-foreground truncate">
          {session.label || session.sessionId.slice(0, 8)}
        </h3>
        <div className="flex items-center gap-1">
          <code className="font-mono text-[10px] text-muted-foreground hidden sm:inline">{session.sessionId.slice(0, 8)}</code>
          <button
            onClick={handleCopySessionId}
            className="text-muted-foreground hover:text-muted-foreground transition-colors"
            title="Copiar session ID"
          >
            {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
          </button>
        </div>

        <button
          onClick={handleCopyJson}
          className="text-muted-foreground hover:text-muted-foreground transition-colors"
          title="Copiar conversa (JSON)"
        >
          {copiedJson ? <Check className="h-3 w-3 text-green-500" /> : <ClipboardCopy className="h-3 w-3" />}
        </button>

        <div className="flex-1" />

        {/* Role filter (all events mode only) */}
        {!isConversation && (
          <select
            value={eventRole}
            onChange={(e) => handleRoleFilter(e.target.value)}
            className="text-xs border border-border rounded-lg px-1.5 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Todos</option>
            <option value="user">Usuario</option>
            <option value="assistant">Assistente</option>
            <option value="toolResult">Resultado</option>
            <option value="system">Sistema</option>
          </select>
        )}

        {/* Thinking toggle */}
        <button
          onClick={() => navigateWithParams({ show_thinking: showThinking ? 'false' : undefined })}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium rounded-lg border transition-colors',
            showThinking
              ? 'border-border text-foreground bg-background hover:bg-accent'
              : 'border-border text-muted-foreground bg-muted hover:bg-accent'
          )}
          title={showThinking ? 'Ocultar pensamento' : 'Mostrar pensamento'}
        >
          {showThinking ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          <Brain className="h-3 w-3" />
        </button>

        {/* Tool calls toggle */}
        <button
          onClick={() => navigateWithParams({ show_tool_calls: showToolCalls ? 'false' : undefined })}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium rounded-lg border transition-colors',
            showToolCalls
              ? 'border-border text-foreground bg-background hover:bg-accent'
              : 'border-border text-muted-foreground bg-muted hover:bg-accent'
          )}
          title={showToolCalls ? 'Ocultar tool calls' : 'Mostrar tool calls'}
        >
          {showToolCalls ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          <Terminal className="h-3 w-3" />
        </button>

        {/* View mode toggle */}
        <div className="bg-accent p-0.5 rounded-lg inline-flex">
          <button
            onClick={() => toggleViewMode('conversation')}
            className={cn(
              'px-2 py-1 text-xs font-medium rounded-md transition-all',
              isConversation
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-accent-foreground'
            )}
          >
            Conversa
          </button>
          <button
            onClick={() => toggleViewMode('all')}
            className={cn(
              'px-2 py-1 text-xs font-medium rounded-md transition-all',
              !isConversation
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-accent-foreground'
            )}
          >
            Todos
          </button>
        </div>
      </div>

      {/* Metadata bar */}
      <div className="text-xs text-muted-foreground px-4 py-1.5 border-b border-border bg-muted flex-shrink-0">
        {stats}
      </div>

      {/* Conversation area */}
      <div ref={conversationRef} className="flex-1 overflow-y-auto px-4 py-4">
        {isConversation ? (
          <ConversationView
            events={events}
            sessionModel={session.model}
            spansMultipleDays={spansMultipleDays}
            showThinking={showThinking}
            showToolCalls={showToolCalls}
          />
        ) : (
          <AllEventsView events={events} spansMultipleDays={spansMultipleDays} showThinking={showThinking} showToolCalls={showToolCalls} />
        )}

        {events.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Nenhum evento encontrado.
          </div>
        )}
      </div>

    </div>
  )
}
